import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hostApiClient } from '../hostApiClient';
import { userApiClient } from '../userApiClient';
import { ROOM_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT } from '../utils/roomBookingNeedsActionCount';
import { getRoomBookingNeedsActionCountFromResponse } from '../utils/roomBookingNeedsActionCountHelpers';

export type RoomBookingNeedsActionScope = 'user' | 'host';

/**
 * Lightweight room-booking needs-action badge total for nav / compact counters.
 * Do not derive from list rows on the client.
 */
export function useRoomBookingNeedsActionCount(
  scope: RoomBookingNeedsActionScope | null,
): number {
  const location = useLocation();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!scope) {
      setCount(0);
      return;
    }

    try {
      const response =
        scope === 'user'
          ? await userApiClient.getRoomBookingsNeedsUserActionCount()
          : await hostApiClient.getHostRoomBookingsNeedsHostActionCount();
      setCount(getRoomBookingNeedsActionCountFromResponse(response));
    } catch {
      // Nav badge is non-critical; keep the last known count on transient errors.
    }
  }, [scope]);

  useEffect(() => {
    void refresh();
  }, [refresh, location.pathname]);

  useEffect(() => {
    if (!scope) {
      return;
    }

    const handleChange = () => {
      void refresh();
    };

    window.addEventListener(ROOM_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT, handleChange);
    return () => window.removeEventListener(ROOM_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT, handleChange);
  }, [scope, refresh]);

  return count;
}
